import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for managing OpenSolar bidirectional synchronization
 */
export function useOpenSolarSync() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<any>(null);
  const { toast } = useToast();

  // Get sync stats
  const {
    data: syncStats,
    isLoading: isLoadingStats,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['opensolar-sync-stats'],
    queryFn: async () => {
      try {
        const response = await apiRequest({ url: '/api/opensolar/status', method: 'GET' });
        return response.syncStats || null;
      } catch (error) {
        console.error('Error fetching OpenSolar sync stats:', error);
        return null;
      }
    },
  });

  // Get projects
  const {
    data: projects = [],
    isLoading: isLoadingProjects,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: ['opensolar-projects'],
    queryFn: async () => {
      try {
        // Get all projects with a large limit and sort by newest first
        const response = await apiRequest({
          url: '/api/opensolar/projects?limit=1000&sortBy=lastSyncedAt&sortOrder=desc',
          method: 'GET',
        });
        console.log('OpenSolar projects response:', response);
        return response.projects || [];
      } catch (error) {
        console.error('Error fetching OpenSolar projects:', error);
        return [];
      }
    },
  });

  /**
   * Perform a one-way sync from OpenSolar to CRM
   */
  const syncFromOpenSolar = async () => {
    setIsSyncing(true);
    try {
      const result = await apiRequest({
        url: '/api/opensolar/sync',
        method: 'POST',
      });

      setLastSyncResult(result);
      await refetchStats();

      toast({
        title: 'Sync Completed',
        description: `Synchronized ${result.projects} projects, ${result.designs} designs, and ${result.quotes} quotes`,
      });

      return result;
    } catch (error) {
      console.error('Error syncing from OpenSolar:', error);
      toast({
        title: 'Sync Failed',
        description: 'Failed to sync data from OpenSolar. Please check the console for details.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  /**
   * Push a single customer to OpenSolar
   */
  const pushCustomerToOpenSolar = async (customerId: number) => {
    setIsSyncing(true);
    try {
      const result = await apiRequest({
        url: `/api/opensolar/push-customer/${customerId}`,
        method: 'POST',
      });

      toast({
        title: result.wasExisting ? 'Customer Already Synced' : 'Customer Pushed to OpenSolar',
        description: result.message,
      });

      await refetchStats();
      return result;
    } catch (error) {
      console.error('Error pushing customer to OpenSolar:', error);
      toast({
        title: 'Push Failed',
        description: 'Failed to push customer to OpenSolar. Please check the console for details.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  /**
   * Perform a bidirectional sync between OpenSolar and CRM
   */
  const syncBidirectional = async () => {
    setIsSyncing(true);
    try {
      const result = await apiRequest({
        url: '/api/opensolar/bidirectional-sync',
        method: 'POST',
      });

      setLastSyncResult(result);
      await refetchStats();

      toast({
        title: 'Bidirectional Sync Completed',
        description: `Synchronized data in both directions. Created ${result.createdProjects} new projects.`,
      });

      return result;
    } catch (error) {
      console.error('Error in bidirectional sync:', error);
      toast({
        title: 'Bidirectional Sync Failed',
        description: 'Failed to sync data. Please check the console for details.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  /**
   * Trigger a sync for a specific project
   */
  const triggerSync = async () => {
    setIsSyncing(true);
    try {
      // Start the sync process
      const result = await syncFromOpenSolar();

      // Refresh projects list
      await refetchProjects();

      return result;
    } catch (error) {
      console.error('Error triggering sync:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  return {
    syncStats,
    projects,
    isSyncing,
    isLoadingStats,
    isLoadingProjects,
    lastSyncResult,
    syncFromOpenSolar,
    pushCustomerToOpenSolar,
    syncBidirectional,
    triggerSync,
    refetchStats,
    refetchProjects,
  };
}
